import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  LogOut,
  RefreshCw,
  Building2,
  Link2,
  Layers,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminPanel({ token, onBack, onLogout }) {
  const [properties, setProperties] = useState([]);
  const [platformTypes, setPlatformTypes] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedPropertyForAssign, setSelectedPropertyForAssign] = useState('');
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // New integration form
  const [newIntFrom, setNewIntFrom] = useState('');
  const [newIntTo, setNewIntTo] = useState('');
  const [newIntDir, setNewIntDir] = useState('two-way');
  const [newIntLabel, setNewIntLabel] = useState('');

  // New assignment form
  const [newAssignPlatform, setNewAssignPlatform] = useState('');
  const [newAssignVendor, setNewAssignVendor] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [propsRes, ptsRes, intsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/properties`, { headers }),
        axios.get(`${BACKEND_URL}/api/platform-types`, { headers }),
        axios.get(`${BACKEND_URL}/api/integrations`, { headers }),
      ]);
      setProperties(propsRes.data);
      setPlatformTypes(ptsRes.data);
      setIntegrations(intsRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch assignments for selected property
  useEffect(() => {
    if (!selectedPropertyForAssign) return;
    const fetchAssignments = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/assignments`, {
          headers,
          params: { property_id: selectedPropertyForAssign }
        });
        setAssignments(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchAssignments();
  }, [selectedPropertyForAssign, headers]);

  const addIntegration = async () => {
    if (!newIntFrom || !newIntTo) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/integrations`, {
        from_platform_id: newIntFrom,
        to_platform_id: newIntTo,
        direction: newIntDir,
        label: newIntLabel,
      }, { headers });
      toast.success('Integration added');
      setNewIntFrom('');
      setNewIntTo('');
      setNewIntLabel('');
      fetchData();
    } catch (err) {
      toast.error('Failed to add integration');
    }
  };

  const deleteIntegration = async (from_id, to_id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/integrations`, {
        headers,
        params: { from_platform_id: from_id, to_platform_id: to_id }
      });
      toast.success('Integration deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const addAssignment = async () => {
    if (!selectedPropertyForAssign || !newAssignPlatform || !newAssignVendor) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/assignments`, {
        property_id: selectedPropertyForAssign,
        platform_type_id: newAssignPlatform,
        vendor_name: newAssignVendor,
      }, { headers });
      toast.success('Assignment saved');
      setNewAssignPlatform('');
      setNewAssignVendor('');
      // Refresh assignments
      const res = await axios.get(`${BACKEND_URL}/api/assignments`, {
        headers,
        params: { property_id: selectedPropertyForAssign }
      });
      setAssignments(res.data);
    } catch (err) {
      toast.error('Failed to save assignment');
    }
  };

  const deleteAssignment = async (platformTypeId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/assignments`, {
        headers,
        params: { property_id: selectedPropertyForAssign, platform_type_id: platformTypeId }
      });
      toast.success('Assignment deleted');
      const res = await axios.get(`${BACKEND_URL}/api/assignments`, {
        headers,
        params: { property_id: selectedPropertyForAssign }
      });
      setAssignments(res.data);
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const getPlatformName = (id) => {
    const pt = platformTypes.find(p => p.id === id);
    return pt?.short || pt?.name || id;
  };

  return (
    <div className="h-screen w-screen flex flex-col bg-background" data-testid="admin-nav">
      {/* Header */}
      <header className="h-14 px-4 md:px-6 flex items-center justify-between border-b bg-card/80 shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Back to Map
          </Button>
          <Separator orientation="vertical" className="h-5" />
          <h1 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Admin Panel
          </h1>
        </div>
        <Button variant="ghost" size="sm" onClick={onLogout} className="gap-1.5">
          <LogOut className="h-3.5 w-3.5" /> Sign Out
        </Button>
      </header>

      <div className="flex-1 overflow-hidden p-4 md:p-6">
        <Tabs defaultValue="assignments" className="h-full flex flex-col">
          <TabsList className="mb-4">
            <TabsTrigger value="assignments" className="gap-1.5">
              <Layers className="h-3.5 w-3.5" /> Assignments
            </TabsTrigger>
            <TabsTrigger value="integrations" className="gap-1.5">
              <Link2 className="h-3.5 w-3.5" /> Integrations
            </TabsTrigger>
            <TabsTrigger value="properties" className="gap-1.5">
              <Building2 className="h-3.5 w-3.5" /> Properties
            </TabsTrigger>
          </TabsList>

          {/* Assignments Tab */}
          <TabsContent value="assignments" className="flex-1 overflow-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Vendor Assignments</CardTitle>
                <CardDescription>Manage which vendor each property uses for each platform</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Label className="text-xs">Property</Label>
                    <Select value={selectedPropertyForAssign} onValueChange={setSelectedPropertyForAssign}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select property" />
                      </SelectTrigger>
                      <SelectContent>
                        {properties.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {selectedPropertyForAssign && (
                  <>
                    <Separator />
                    {/* Add new assignment */}
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <Label className="text-xs">Platform</Label>
                        <Select value={newAssignPlatform} onValueChange={setNewAssignPlatform}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select platform" />
                          </SelectTrigger>
                          <SelectContent>
                            {platformTypes.map(pt => (
                              <SelectItem key={pt.id} value={pt.id}>{pt.short || pt.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs">Vendor</Label>
                        <Input
                          value={newAssignVendor}
                          onChange={e => setNewAssignVendor(e.target.value)}
                          placeholder="Vendor name"
                        />
                      </div>
                      <Button size="sm" onClick={addAssignment} className="gap-1" data-testid="admin-save-button">
                        <Save className="h-3.5 w-3.5" /> Save
                      </Button>
                    </div>

                    {/* Current assignments */}
                    <div className="space-y-1" data-testid="assignment-editor">
                      {assignments.map((a, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/40 text-xs">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px]">{getPlatformName(a.platform_type_id)}</Badge>
                            <span className="font-mono">{a.vendor_name}</span>
                          </div>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => deleteAssignment(a.platform_type_id)}>
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </div>
                      ))}
                      {assignments.length === 0 && (
                        <p className="text-xs text-muted-foreground py-4 text-center">No assignments for this property</p>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Integrations Tab */}
          <TabsContent value="integrations" className="flex-1 overflow-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Platform Integrations</CardTitle>
                <CardDescription>Define connections between platform types</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="text-xs">From</Label>
                    <Select value={newIntFrom} onValueChange={setNewIntFrom}>
                      <SelectTrigger><SelectValue placeholder="Source" /></SelectTrigger>
                      <SelectContent>
                        {platformTypes.map(pt => (
                          <SelectItem key={pt.id} value={pt.id}>{pt.short || pt.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">To</Label>
                    <Select value={newIntTo} onValueChange={setNewIntTo}>
                      <SelectTrigger><SelectValue placeholder="Target" /></SelectTrigger>
                      <SelectContent>
                        {platformTypes.map(pt => (
                          <SelectItem key={pt.id} value={pt.id}>{pt.short || pt.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-32">
                    <Label className="text-xs">Direction</Label>
                    <Select value={newIntDir} onValueChange={setNewIntDir}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="one-way">One-way</SelectItem>
                        <SelectItem value="two-way">Two-way</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Label</Label>
                    <Input value={newIntLabel} onChange={e => setNewIntLabel(e.target.value)} placeholder="Description" />
                  </div>
                  <Button size="sm" onClick={addIntegration} className="gap-1">
                    <Plus className="h-3.5 w-3.5" /> Add
                  </Button>
                </div>

                <Separator />

                <ScrollArea className="h-[400px]">
                  <div className="space-y-1" data-testid="integrations-table">
                    {integrations.map((integ, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/40 text-xs">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px]">{getPlatformName(integ.from_platform_id)}</Badge>
                          <span className="text-muted-foreground">
                            {integ.direction === 'two-way' ? '↔' : '→'}
                          </span>
                          <Badge variant="outline" className="text-[10px]">{getPlatformName(integ.to_platform_id)}</Badge>
                          {integ.label && <span className="text-muted-foreground">({integ.label})</span>}
                        </div>
                        <Button
                          variant="ghost" size="icon" className="h-6 w-6"
                          onClick={() => deleteIntegration(integ.from_platform_id, integ.to_platform_id)}
                        >
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Properties Tab */}
          <TabsContent value="properties" className="flex-1 overflow-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Properties</CardTitle>
                <CardDescription>Manage hotel properties in the system</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-1" data-testid="properties-table">
                    {properties.map(p => (
                      <div key={p.id} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/40 text-sm">
                        <div>
                          <span className="font-medium">{p.name}</span>
                          <Badge variant="secondary" className="ml-2 text-[10px]">{p.type}</Badge>
                        </div>
                        <span className="text-xs text-muted-foreground font-mono">{p.id}</span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
